import { VideoEvent } from "../../../../../utilities/useTimestamps";

// --- Event predicates ---

const COLLABORATION_EVENT_NAME = "collaboration detected";
const ESSAY_COLLABORATION_EVENT_NAME = "essay collaboration detected";

export const isCollaborationDetected = (event: VideoEvent) =>
	event.type === "assessment" &&
	(event.name === COLLABORATION_EVENT_NAME || event.name === ESSAY_COLLABORATION_EVENT_NAME);

const isEssayCollaborationEvent = (event: VideoEvent) => event.name === ESSAY_COLLABORATION_EVENT_NAME;

// --- Shared shapes ---

export type CollaborationTimespan = { start: number; end: number };

/**
 * One side of a collaboration pair, as recorded by the backend (see collaboration-detection.ts and
 * collaboration-detection-essay.ts). The essay flow doesn't track question-navigation timing, so
 * `questionTimes` is only ever populated by the non-essay flow.
 */
export type CollaborationParticipant = {
	proctoringSessionId: number;
	studentId?: number;
	studentName?: string;
	/** Keyed by question number; the timespans this student spent on that question. */
	questionTimes?: Record<number, CollaborationTimespan[]>;
};

// --- Regular-flow raw event details ---

/** The subject's quiz response row for the flagged question, as recorded by the backend. */
export type CollaborationQuestionDetails = {
	id: number;
	sessionId: number;
	questionNo: number;
	questionSnapshot: unknown;
	answer: unknown;
	isAnswered: boolean;
	createdAt: string;
	updatedAt: string;
};

/**
 * `subject` is the student that owns the proctoring session this event belongs to; `partner` is
 * the other student they were flagged for collaborating with. `question` is the one flagged
 * question this event covers, including what the subject answered on it.
 */
type RawCollaborationDetails = {
	subject: CollaborationParticipant;
	partner: CollaborationParticipant;
	question: CollaborationQuestionDetails;
	totalQuestions?: number;
};

// --- Essay-flow raw event details ---

/** One student's essay response, as recorded by the backend (see collaboration-detection-essay.ts). */
export type EssayCollaborationResponse = {
	id: number;
	response: string;
};

/** One matching passage between the subject's and partner's essay answers, as recorded by the backend
 *  (see collaboration-detection-essay.ts). Ranges are word offsets into each student's answer text. */
export type EssayCollaborationChunk = {
	thisRange: [number, number];
	otherRange: [number, number];
	jaccard: number;
	levenshtein: number;
	jaccardMet: boolean;
	levenshteinMet: boolean;
};

/** The flagged essay question and the passages the subject's response shares with the partner's. */
export type EssayCollaborationMatch = {
	questionId: number;
	questionSnapshot: unknown;
	subjectResponse: EssayCollaborationResponse;
	partnerResponse: EssayCollaborationResponse;
	chunks: EssayCollaborationChunk[];
};

/** Same shape as {@link RawCollaborationDetails}, but as recorded by the essay-similarity flow. */
type RawEssayCollaborationDetails = {
	subject: CollaborationParticipant;
	partner: CollaborationParticipant;
	match: EssayCollaborationMatch;
	totalQuestions?: number;
};

type RawDetails = RawCollaborationDetails | RawEssayCollaborationDetails;

function getRawDetails(event: VideoEvent): RawDetails {
	return event.details as RawDetails;
}

/** The id of the one flagged question an event covers, whichever detection flow recorded it. */
function getQuestionId(details: RawDetails): number {
	return "question" in details ? details.question.questionNo : details.match.questionId;
}

// --- Grouped output shapes ---

export type CollaborationPartnerSummary = { studentId?: number; studentName?: string };

/** Details for the seekbar's one-marker-per-question grouping. */
export type CollaborationQuestionGroupedDetails = {
	questionNo: number;
	partners: CollaborationPartnerSummary[];
};

/**
 * One flagged question in the table's per-partner grouping, tagged by which detection flow found it:
 * `regular` questions carry the subject's actual quiz response for answer-correctness evidence;
 * `essay` questions carry the matched passages for text-similarity evidence instead.
 */
export type CollaborationFlaggedQuestion =
	{ kind: "regular"; details: CollaborationQuestionDetails } | { kind: "essay"; details: EssayCollaborationMatch };

/** Details for the table's one-row-per-partner grouping. */
export type CollaborationPartnerGroupedDetails = {
	subject: CollaborationParticipant;
	partner: CollaborationParticipant;
	questions: CollaborationFlaggedQuestion[];
	totalQuestions?: number;
};

// --- Grouping ---

/** Splits events into the ones this module groups and everything else, in a single pass. */
function partitionCollaborationEvents(events: VideoEvent[]): [collaborationEvents: VideoEvent[], others: VideoEvent[]] {
	const collaborationEvents: VideoEvent[] = [];
	const others: VideoEvent[] = [];
	for (const event of events) {
		(isCollaborationDetected(event) ? collaborationEvents : others).push(event);
	}
	return [collaborationEvents, others];
}

/** Keeps whichever side already has `questionTimes` — a later event for the same partner shouldn't
 *  erase timing data an earlier (non-essay) event already established. */
function mergeParticipant(
	existing: CollaborationParticipant,
	next: CollaborationParticipant,
): CollaborationParticipant {
	return { ...next, questionTimes: next.questionTimes ?? existing.questionTimes };
}

type CollaborationQuestionEvent = Omit<VideoEvent, "details"> & { details: CollaborationQuestionGroupedDetails };

/**
 * The backend logs one "collaboration detected" (or "essay collaboration detected") event per
 * (question, partner) pair. The seekbar should surface a single marker per flagged question, naming
 * every partner implicated on it.
 */
export function groupCollaborationEventsByQuestion(events: VideoEvent[]): VideoEvent[] {
	const [collaborationEvents, others] = partitionCollaborationEvents(events);
	const byQuestion = new Map<string, CollaborationQuestionEvent>();

	for (const event of collaborationEvents) {
		const details = getRawDetails(event);
		const partner: CollaborationPartnerSummary = {
			studentId: details.partner.studentId,
			studentName: details.partner.studentName,
		};

		// A regular question's questionNo (its position in the quiz) and an essay question's questionId
		// (its own database id) come from different id spaces, so namespace the map key to keep a
		// coincidental numeric collision from merging two unrelated questions into one marker.
		const questionNo = getQuestionId(details);
		const groupKey = `${isEssayCollaborationEvent(event) ? "essay" : "regular"}:${questionNo}`;

		const existing = byQuestion.get(groupKey);
		if (existing) {
			existing.details.partners.push(partner);
		} else {
			byQuestion.set(groupKey, {
				...event,
				key: `collaboration-question-${groupKey}`,
				details: { questionNo, partners: [partner] },
			});
		}
	}

	return [...others, ...byQuestion.values()];
}

type CollaborationPartnerEvent = Omit<VideoEvent, "details"> & { details: CollaborationPartnerGroupedDetails };

/**
 * The table should surface a single row per collaborating partner, listing every question — essay or
 * otherwise — they were flagged on together.
 */
export function groupCollaborationEventsByPartner(events: VideoEvent[]): VideoEvent[] {
	const [collaborationEvents, others] = partitionCollaborationEvents(events);
	const byPartner = new Map<number, CollaborationPartnerEvent>();

	for (const event of collaborationEvents) {
		const details = getRawDetails(event);
		const flaggedQuestion: CollaborationFlaggedQuestion = isEssayCollaborationEvent(event)
			? { kind: "essay", details: (details as RawEssayCollaborationDetails).match }
			: { kind: "regular", details: (details as RawCollaborationDetails).question };

		const existing = byPartner.get(details.partner.proctoringSessionId);
		if (!existing) {
			byPartner.set(details.partner.proctoringSessionId, {
				...event,
				key: `collaboration-partner-${details.partner.proctoringSessionId}`,
				details: {
					subject: details.subject,
					partner: details.partner,
					questions: [flaggedQuestion],
					totalQuestions: details.totalQuestions,
				},
			});
			continue;
		}

		existing.details.questions.push(flaggedQuestion);
		existing.details.subject = mergeParticipant(existing.details.subject, details.subject);
		existing.details.partner = mergeParticipant(existing.details.partner, details.partner);
		// Keeps whichever event already has `totalQuestions` — a property of the quiz, so every event for
		// this partner shares the same value; an older event recorded before this field existed shouldn't
		// blank out a value a newer event already set.
		existing.details.totalQuestions = existing.details.totalQuestions ?? details.totalQuestions;
		if (event.time > existing.time) {
			existing.time = event.time;
		}
	}

	return [...others, ...byPartner.values()];
}
